import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import { Search, UserPlus, Check, Ban, ChevronLeft, ChevronRight } from 'lucide-react';
import { httpClient } from '@/api';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'PATIENT' | 'DOCTOR' | 'ADMIN';
  status: 'ACTIVE' | 'PENDING' | 'BLOCKED';
  createdAt: string;
  verified: boolean;
  doctor: { id: string; crm: string; displayName: string | null; specialty: string | null; status: string } | null;
  patient: { id: string } | null;
}

interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  pageSize: number;
}

interface AdminUserApiResponse {
  data: Array<{
    id: string;
    email: string;
    phone: string;
    role: string;
    status: string;
    createdAt: string;
    doctor: { id: string; crm: string; displayName: string | null; specialty: string | null; status: string } | null;
    patient: { id: string } | null;
  }>;
  meta: { page: number; perPage: number; total: number; pages: number };
}

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'ACTIVE' | 'BLOCKED' | 'DOCTOR'>('ALL');

  const { data, isLoading, isError, refetch } = useQuery<UsersResponse>({
    queryKey: ['admin', 'users', page, filter, search],
    queryFn: async (): Promise<UsersResponse> => {
      const params = new URLSearchParams({
        page: String(page),
        limit: '10',
      });
      if (filter !== 'ALL') params.append('role', filter);

      const json = await httpClient.get<AdminUserApiResponse>(`/admin/users?${params}`);

      return {
        users: json.data.map((user) => {
          const name = user.doctor?.displayName ?? user.email.split('@')[0];
          return {
            id: user.id,
            name: name || 'Unknown',
            email: user.email,
            role: user.role as 'PATIENT' | 'DOCTOR' | 'ADMIN',
            status: user.status as 'ACTIVE' | 'PENDING' | 'BLOCKED',
            createdAt: user.createdAt,
            verified: user.status === 'ACTIVE',
            doctor: user.doctor,
            patient: user.patient,
          };
        }),
        total: json.meta.total,
        page: json.meta.page,
        pageSize: json.meta.perPage,
      };
    },
  });

  const activateUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return httpClient.patch(`/admin/users/${userId}/approve`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const blockUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return httpClient.patch(`/admin/users/${userId}/block`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const filteredUsers = data?.users.filter((user) => {
    const matchesSearch = user.name.toLowerCase().includes(search.toLowerCase()) ||
                          user.email.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'ALL' ||
                          (filter === 'PENDING' && user.status === 'PENDING') ||
                          (filter === 'ACTIVE' && user.status === 'ACTIVE') ||
                          (filter === 'BLOCKED' && user.status === 'BLOCKED') ||
                          (filter === 'DOCTOR' && user.role === 'DOCTOR');
    return matchesSearch && matchesFilter;
  }) || [];

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Gerenciamento de Usuários</h1>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <UserPlus className="h-4 w-4" />
          Adicionar Usuário
        </button>
      </div>

      <div className="flex items-center flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="ALL">Todos</option>
          <option value="PENDING">Pendentes</option>
          <option value="ACTIVE">Ativos</option>
          <option value="BLOCKED">Bloqueados</option>
          <option value="DOCTOR">Médicos</option>
        </select>
      </div>

      {isLoading ? (
        <LoadingOverlay message="Carregando usuários..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar usuários" onRetry={() => refetch()} />
      ) : filteredUsers.length === 0 ? (
        <EmptyState
          icon="Users"
          title="Nenhum usuário encontrado"
          description={search || filter !== 'ALL' ? 'Tente ajustar os filtros de busca' : 'Nenhum usuário cadastrado ainda'}
          action={{ label: 'Atualizar', onClick: () => refetch() }}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Nome</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Tipo</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Data</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium">
                            {user.name.charAt(0)}
                          </div>
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{user.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.role === 'DOCTOR' ? 'bg-purple-100 text-purple-700' :
                          user.role === 'ADMIN' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {user.role === 'DOCTOR' ? 'Médico' : user.role === 'ADMIN' ? 'Admin' : 'Paciente'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                          user.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {user.status === 'ACTIVE' ? 'Ativo' : user.status === 'PENDING' ? 'Pendente' : 'Bloqueado'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(user.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {user.status === 'PENDING' && (
                            <button
                              onClick={() => activateUserMutation.mutate(user.id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Aprovar"
                            >
                              <Check className="h-5 w-5" />
                            </button>
                          )}
                          {user.status === 'ACTIVE' && (
                            <button
                              onClick={() => blockUserMutation.mutate(user.id)}
                              className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                              title="Bloquear"
                            >
                              <Ban className="h-5 w-5" />
                            </button>
                          )}
                          {user.status === 'BLOCKED' && (
                            <button
                              onClick={() => activateUserMutation.mutate(user.id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Desbloquear"
                            >
                              <Check className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-gray-500">
                Mostrando {((page - 1) * 10) + 1} a {Math.min(page * 10, data?.total || 0)} de {data?.total} usuários
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-3 py-1 bg-blue-600 text-white rounded-lg">{page}</span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * 10 >= (data?.total || 0)}
                  className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
